import React, { useEffect, useReducer, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { getApiErrorMessage } from 'app/util'
import { UserLayout } from 'components/layout/UserLayout'
import { TaskPageBar } from 'components/layout/PageBar'
import { FullPageContent } from 'components/layout/FullPageLayout/Content/FullPageContent'
import {
  buildConfirmRows,
  creativeImportReducer,
  initialCreativeImportState,
  previewObjectUrls,
  RowMediaPreview,
} from './state'
import { extractRowThumbnails } from './thumbnails'
import { useCreativeImportValidate } from './useCreativeImportValidate'
import { useCreativeImportConfirm } from './useCreativeImportConfirm'
import { useCreativeImportRetry } from './useCreativeImportRetry'
import { fetchCreativeImportProgress, useCreativeImportProgress } from './useCreativeImportProgress'
import { fetchCreativeImportEditState } from './useCreativeImportEditState'
import { fetchStagedArchive } from './fetchStagedArchive'
import { useCreativeImportAutosave } from './useCreativeImportAutosave'
import {
  CreativeImportJobStatus,
  CreativeImportProgress,
  CreativeImportRowStatus,
  CreativeImportValidationReport,
} from '@marketing-milk/interfaces'
import { UploadStep } from './steps/UploadStep'
import { ReviewStep } from './steps/ReviewStep'
import { OptionsStep } from './steps/OptionsStep'
import { ImportStep } from './steps/ImportStep'
import { SummaryStep } from './steps/SummaryStep'

export function CreativeImportPage() {
  const history = useHistory()
  const queryClient = useQueryClient()
  const businessID = useGetBusinessID()
  const { creativeImportID: importIDParam } = useParams<{ creativeImportID?: string }>()
  const [state, dispatch] = useReducer(creativeImportReducer, initialCreativeImportState)
  const validateMutation = useCreativeImportValidate()
  const confirmMutation = useCreativeImportConfirm()
  const retryMutation = useCreativeImportRetry()
  const previewsRef = useRef<Record<number, RowMediaPreview[]>>({})
  previewsRef.current = state.previews
  // Bumped on every new upload/reset so in-flight work from an abandoned
  // upload can't populate the next one's state.
  const uploadTokenRef = useRef(0)

  // Rebuild Review's previews for a reopened import by re-downloading the
  // staged archive and running it through the same client-side extractor as
  // upload. A failure leaves Review usable with placeholders, so it's
  // swallowed. If the caller moved on before extraction finished, revoke.
  const loadThumbnails = (
    importID: number,
    report: CreativeImportValidationReport,
    isStale: () => boolean
  ) => {
    fetchStagedArchive(businessID, importID)
      .then(archive => extractRowThumbnails(archive, report))
      .then(thumbnails => {
        if (isStale()) {
          previewObjectUrls(thumbnails).forEach(url => URL.revokeObjectURL(url))
          return
        }
        dispatch({ type: 'thumbnailsLoaded', thumbnails })
      })
      .catch(() => {})
  }

  // Reopening a job by URL. Keyed on the param — the active-import banner links
  // between two URLs of this same route, so the page instance is reused and a
  // mount-only effect would not re-run. A confirmed job jumps to the live Import
  // view (running) or Summary (terminal); an unconfirmed job rehydrates its
  // pre-confirm Review state (CR); an unknown/errored id falls back to a clean URL.
  useEffect(() => {
    const importID = importIDParam ? Number(importIDParam) : null
    if (importID === null || Number.isNaN(importID) || importID === state.creativeImportID) {
      return
    }
    let cancelled = false
    fetchCreativeImportProgress(businessID, importID)
      .then(async progress => {
        if (cancelled) return
        // CR: an unconfirmed import resumes its pre-confirm Review state (edits +
        // selection + sprint) instead of starting over.
        if (progress.status === CreativeImportJobStatus.AwaitingConfirmation) {
          const editState = await fetchCreativeImportEditState(businessID, importID)
          if (cancelled) return
          dispatch({ type: 'hydratePreConfirm', editState })
          loadThumbnails(importID, editState.report, () => cancelled)
          return
        }
        dispatch({ type: 'hydrateFromProgress', progress })
      })
      .catch(() => {
        if (!cancelled) history.replace(`/${businessID}/creatives/import`)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importIDParam])

  // The job is addressable the moment validate persists it — put its id in the
  // URL immediately (CR: so a refresh during Review can reopen and rehydrate,
  // not just after confirm). The reopen effect's `importID === creativeImportID`
  // guard keeps this from re-triggering a fetch. replace: it's one session.
  useEffect(() => {
    if (state.creativeImportID !== null) {
      history.replace(`/${businessID}/creatives/import/${state.creativeImportID}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.creativeImportID])

  // CR: autosave the pre-confirm edit state to the persisted job.
  const autosave = useCreativeImportAutosave(businessID, {
    creativeImportID: state.creativeImportID,
    // Failed-row edits save to a confirmed job; the server only takes their copy.
    confirmed: state.confirmed && !state.editingFailedRows,
    report: state.report,
    rows: state.rows,
    sprintID: state.sprintID,
    createPacket: state.createPacket,
  })

  // Flush on every step change so a step transition never leaves the last edit
  // only in the debounce window (the pre-confirm Import step is included).
  useEffect(() => {
    void autosave.flush()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step])

  // Poll only after the backend accepted the confirm — earlier polling would
  // populate `progress` and skip the Import step's confirmation copy.
  const pollingActive =
    (state.step === 'import' || state.step === 'summary') && state.confirmed && !state.confirming
  const progressQuery = useCreativeImportProgress(state.creativeImportID, pollingActive)
  const progress = progressQuery.data
  useEffect(() => {
    if (progress) dispatch({ type: 'progressUpdated', progress })
  }, [progress])

  const revokeThumbnails = () => {
    previewObjectUrls(previewsRef.current).forEach(url => URL.revokeObjectURL(url))
  }

  const reset = () => {
    uploadTokenRef.current += 1
    revokeThumbnails()
    dispatch({ type: 'reset' })
    history.replace(`/${businessID}/creatives/import`)
  }

  // Invalidate async upload work and revoke object URLs when the page unmounts.
  useEffect(
    () => () => {
      uploadTokenRef.current += 1
      revokeThumbnails()
    },
    []
  )

  const handleDone = () => {
    history.push(`/${businessID}/creatives`)
  }

  const handleRetryFailed = async () => {
    if (state.creativeImportID === null) return
    try {
      await retryMutation.mutateAsync(state.creativeImportID)
      // Polling stopped at the terminal status, so one manual fetch is what sees
      // the re-queued job and restarts the interval.
      await progressQuery.refetch()
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'The failed rows could not be retried.'))
    }
  }

  const handleEditFailed = async () => {
    if (state.creativeImportID === null) return
    const importID = state.creativeImportID
    const token = uploadTokenRef.current
    const isStale = () => token !== uploadTokenRef.current
    try {
      const editState = await fetchCreativeImportEditState(businessID, importID)
      if (isStale()) return
      dispatch({ type: 'editFailedRows', editState })
      loadThumbnails(importID, editState.report, isStale)
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'The failed rows could not be opened.'))
    }
  }

  const handleBackToSummary = async () => {
    const token = uploadTokenRef.current
    if (!(await autosave.flush())) {
      toast.error("Your changes couldn't be saved. Try again before leaving the editor.")
      return
    }
    if (token !== uploadTokenRef.current) return
    uploadTokenRef.current += 1
    revokeThumbnails()
    dispatch({ type: 'backToSummary' })
  }

  // The retry runs on the saved rows, so an edit that didn't save must stop it.
  const handleRetryEdited = async () => {
    if (state.creativeImportID === null || !state.progress) return
    const importID = state.creativeImportID
    const token = uploadTokenRef.current
    if (!(await autosave.flush())) {
      toast.error("Your changes couldn't be saved, so the rows weren't retried. Try again.")
      return
    }
    if (token !== uploadTokenRef.current) return
    try {
      await retryMutation.mutateAsync(importID)
      if (token !== uploadTokenRef.current) return
      // The POST has accepted the retry. Seed the polling cache with that
      // state so a failed progress GET cannot leave us editing a running job
      // or keep polling stopped on the previous terminal result.
      const progress: CreativeImportProgress = {
        ...state.progress,
        status: CreativeImportJobStatus.Queued,
        errorMessage: undefined,
        rows: state.progress.rows.map(row =>
          row.status === CreativeImportRowStatus.Error
            ? { ...row, status: CreativeImportRowStatus.Pending }
            : row
        ),
        counts: {
          ...state.progress.counts,
          error: 0,
          remaining: state.progress.counts.remaining + state.progress.counts.error,
        },
      }
      queryClient.setQueryData(['creative-import-progress', Number(businessID), importID], progress)
      uploadTokenRef.current += 1
      revokeThumbnails()
      dispatch({ type: 'hydrateFromProgress', progress })
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'The failed rows could not be retried.'))
    }
  }

  const handleFile = async (file: File) => {
    revokeThumbnails()
    const token = ++uploadTokenRef.current
    const isStale = () => token !== uploadTokenRef.current
    dispatch({ type: 'validateStart', file })
    try {
      const report = await validateMutation.mutateAsync(file)
      if (isStale()) return
      dispatch({ type: 'validateSuccess', report })
      if (!report.archiveErrors.length) {
        const thumbnails = await extractRowThumbnails(file, report)
        if (isStale()) {
          // Nobody owns these URLs anymore — release them here.
          previewObjectUrls(thumbnails).forEach(url => URL.revokeObjectURL(url))
          return
        }
        dispatch({ type: 'thumbnailsLoaded', thumbnails })
      }
    } catch (e: any) {
      if (isStale()) return
      const message =
        e?.response?.data?.message ??
        'The archive could not be validated. Check the file and try again.'
      dispatch({
        type: 'validateError',
        message: Array.isArray(message) ? message.join(' ') : message,
      })
    }
  }

  const handleConfirm = async () => {
    if (state.creativeImportID === null || !state.report) return
    const token = uploadTokenRef.current
    const isStale = () => token !== uploadTokenRef.current
    const rows = buildConfirmRows(state.report, state.rows)
    // Persist any pending edits and cancel the debounce before confirming, so a
    // stale autosave can't land after the job leaves awaiting_confirmation (CR).
    await autosave.flush()
    if (isStale()) return
    dispatch({ type: 'confirmStart' })
    try {
      await confirmMutation.mutateAsync({
        creativeImportID: state.creativeImportID,
        request: {
          rows,
          sprintID: state.sprintID ?? undefined,
          // S6b: only meaningful with a sprint; the backend ignores it otherwise.
          createPacket: state.sprintID !== null && state.createPacket,
        },
      })
      if (isStale()) return
      dispatch({ type: 'confirmSuccess' })
    } catch (e: any) {
      if (isStale()) return
      // 409 = already confirmed (double-click/second tab) — just poll.
      if (e?.response?.status === 409) {
        dispatch({ type: 'confirmSuccess' })
        return
      }
      const message =
        e?.response?.data?.message ?? 'The import could not be started. Please try again.'
      dispatch({
        type: 'confirmError',
        message: Array.isArray(message) ? message.join(' ') : message,
      })
    }
  }

  return (
    <UserLayout>
      <TaskPageBar title="Import Ads" closeTo={`/${businessID}/creatives`} />

      <FullPageContent className="flex flex-col sm:p-4">
        {/* Review is a three-column master-detail; the other steps are single
            columns that read badly when stretched, so only Review gets the width. */}
        <div
          className={`w-full mx-auto ${
            state.step === 'review' ? 'max-w-screen-2xl' : 'max-w-screen-lg'
          }`}
        >
          {state.step === 'upload' && <UploadStep state={state} onFile={handleFile} />}
          {state.step === 'review' && (
            <ReviewStep
              state={state}
              dispatch={dispatch}
              onStartOver={reset}
              onBackToSummary={handleBackToSummary}
              onRetryFailed={handleRetryEdited}
              retrying={retryMutation.isLoading}
            />
          )}
          {state.step === 'options' && (
            <OptionsStep
              state={state}
              dispatch={dispatch}
              onContinue={() => dispatch({ type: 'goToImport' })}
            />
          )}
          {state.step === 'import' && (
            <ImportStep
              state={state}
              onConfirm={handleConfirm}
              onBack={() => dispatch({ type: 'backToOptions' })}
            />
          )}
          {state.step === 'summary' && (
            <SummaryStep
              state={state}
              onImportAnother={reset}
              onDone={handleDone}
              onRetryFailed={handleRetryFailed}
              onEditFailed={handleEditFailed}
              retrying={retryMutation.isLoading}
            />
          )}
          {/* Below the step, not above it: on Review every pixel above the fold
              belongs to the ad being checked. */}
          {(state.step === 'review' || state.step === 'options') && autosave.status !== 'idle' && (
            <div className="text-xs text-right mt-2" data-testid="autosave-status">
              {autosave.status === 'saving' && <span className="text-muted">Saving…</span>}
              {autosave.status === 'saved' && <span className="text-muted">All changes saved</span>}
              {autosave.status === 'error' && (
                <button
                  type="button"
                  className="text-danger underline"
                  onClick={() => void autosave.flush()}
                >
                  Couldn&apos;t save your changes — retry
                </button>
              )}
            </div>
          )}
        </div>
      </FullPageContent>
    </UserLayout>
  )
}
